// Login.js - PRODUCCIÓN
import React, { useState, useEffect, useRef } from 'react';
import { Shield, AlertTriangle, Eye, EyeOff, ArrowRight, X, Check } from 'lucide-react';
import logo from './imgs/Logo_Fcn.png';
import logoBlanco from './imgs/Logo_Fcn_blanco.png';
import ChangePassword from './ChangePassword';
import CredentialRequestModal from './modals/CredentialRequestModal';
import PasswordResetModal from './modals/PasswordResetModal';
import ErrorAlert from './components/ErrorAlert';
import { readJson, responseError, describeError } from './utils/http';

// Estilo compartido de los campos de usuario y contraseña (icono a la izquierda)
const INPUT_CLASS =
  'login-input w-full h-12 px-4 bg-[#FBF8F5] border border-neutral-50 rounded-xl text-[15px] text-cafe-800 placeholder:text-neutral-200 shadow-[inset_0_1px_2px_rgba(62,39,35,0.04)] hover:border-cafe-200/70 focus:outline-none focus:bg-white focus:border-cafe-200 focus:ring-4 focus:ring-cafe-200/50 focus:-translate-y-0.5 focus:shadow-[0_12px_24px_-12px_rgba(62,39,35,0.45)] transition-all duration-500 ease-out';

// Patrón de granos de café en línea fina, muy tenue, sobre el fondo claro de la página
// Mosaico de 6x6 granos con posición y giro pseudoaleatorios (semilla fija, siempre igual).
const BEAN_GRID = 6;
const BEAN_CELL = 85;
const mulberry32 = (a) => () => {
  let t = (a += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const rand = mulberry32(7);
const BEANS = Array.from({ length: BEAN_GRID * BEAN_GRID }, (_, i) => {
  const col = i % BEAN_GRID;
  const row = Math.floor(i / BEAN_GRID);
  return [
    Math.round(col * BEAN_CELL + BEAN_CELL / 2 + (rand() - 0.5) * 30),
    Math.round(row * BEAN_CELL + BEAN_CELL / 2 + (rand() - 0.5) * 30),
    Math.round(rand() * 180 - 90),
  ];
});
const bean = (x, y, rot) =>
  `<g transform="rotate(${rot} ${x} ${y})"><ellipse cx="${x}" cy="${y}" rx="10" ry="6"/><path d="M${x - 10} ${y} Q${x - 5} ${y - 4} ${x} ${y} T${x + 10} ${y}"/></g>`;
const BEAN_TILE = BEAN_GRID * BEAN_CELL;
const beanPattern = (stroke, opacity) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${BEAN_TILE}" height="${BEAN_TILE}" viewBox="0 0 ${BEAN_TILE} ${BEAN_TILE}"><g fill="none" stroke="${stroke}" stroke-opacity="${opacity}" stroke-width="1.2" stroke-linecap="round">${BEANS.map(([x, y, rot]) => bean(x, y, rot)).join('')}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
const BEAN_PATTERN = beanPattern('#7B5B3E', 0.06);

// Fondo claro de la página y café institucional del panel de bienvenida
const FONDO = '#F1E8DC';
const CAFE_PANEL = '#4E3221';

// Mismos tramos horarios que el saludo de PortalMain
const getGreeting = (date) => {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 22) return 'Buenas tardes';
  return 'Buenas noches';
};
// Fecha en una sola línea, p. ej. "Lunes · 12 oct 2026" (se muestra en mayúsculas)
const WEEKDAY_FORMAT = new Intl.DateTimeFormat('es-CO', { weekday: 'long' });
const MONTH_FORMAT = new Intl.DateTimeFormat('es-CO', { month: 'short' });
const formatPanelDate = (date) =>
  `${WEEKDAY_FORMAT.format(date)} · ${date.getDate()} ${MONTH_FORMAT.format(date).replace('.', '')} ${date.getFullYear()}`;

// "Recordar mi usuario": solo se guarda el nombre de usuario, nunca la contraseña.
// El acceso al storage puede fallar (modo privado o bloqueado), por eso va en try/catch.
export const REMEMBERED_USER_KEY = 'portalRememberedUser';
const readRememberedUser = () => {
  try {
    return localStorage.getItem(REMEMBERED_USER_KEY) || '';
  } catch {
    return '';
  }
};
const saveRememberedUser = (remember, user) => {
  try {
    if (remember) localStorage.setItem(REMEMBERED_USER_KEY, user);
    else localStorage.removeItem(REMEMBERED_USER_KEY);
  } catch {
    // Sin storage disponible simplemente no se recuerda
  }
};

const decodeJWT = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Token JWT inválido');
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    return null;
  }
};

const establecerCookieSesion = (token) => {
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.sessionToken) return false;
    
    const sessionToken = decoded.sessionToken;
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
    
    const cookieValue = `sessionId=${sessionToken}`;
    const cookieOptions = [];
    
    if (isDevelopment) {
      cookieOptions.push(`path=/`);
      cookieOptions.push(`max-age=54000`);
    } else {
      cookieOptions.push(`domain=.aplicacionesfcn.com`);
      cookieOptions.push(`path=/`);
      cookieOptions.push(`max-age=54000`);
      cookieOptions.push(`secure=true`);
      cookieOptions.push(`samesite=None`);
    }
    
    document.cookie = `${cookieValue}; ${cookieOptions.join('; ')}`;
    return document.cookie.includes('sessionId=');
  } catch (error) {
    return false;
  }
};

const Login = ({ onLogin, initialError, onErrorCleared }) => {
  const [rememberedUser] = useState(readRememberedUser);
  const [username, setUsername] = useState(rememberedUser);
  const [rememberUser, setRememberUser] = useState(Boolean(rememberedUser));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorKind, setErrorKind] = useState('user');
  const [isLoading, setIsLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [userForPasswordChange, setUserForPasswordChange] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [sessionExpiredInfo, setSessionExpiredInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const usernameRef = useRef(null);

  // Refresca saludo y fecha si la pantalla queda abierta al cambiar de franja o de día
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (error) usernameRef.current?.focus();
  }, [error]);

  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      setSessionExpiredInfo({ show: true, reason: sessionStorage.getItem('sessionExpiredReason') });
      sessionStorage.removeItem('sessionExpired');
      sessionStorage.removeItem('sessionExpiredReason');
    }
  }, []);

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  useEffect(() => {
    if (window.location.hash || window.location.search) {
      window.history.replaceState({ page: 'login' }, 'Login', window.location.pathname);
    }
  }, []);

  const clearError = () => {
    setError('');
    setErrorKind('user');
    setSuccessMessage('');
    if (onErrorCleared) onErrorCleared();
  };

  const handlePasswordChanged = () => {
    setShowChangePassword(false);
    setUserForPasswordChange('');
    setSuccessMessage('Contraseña cambiada exitosamente. Iniciando sesión...');
    setUsername(userForPasswordChange);
    setPassword('');
    setTimeout(() => {
      handleSubmit(new Event('submit'));
    }, 1000);
  };

  const handleCancelPasswordChange = () => {
    setShowChangePassword(false);
    setUserForPasswordChange('');
    setUsername('');
    setPassword('');
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    if (!username) {
      setError('Por favor ingrese su nombre de usuario');
      setIsLoading(false);
      return;
    }

    if (!password) {
      setError('Por favor ingrese su contraseña');
      setIsLoading(false);
      return;
    }

    try {
      const loginTimestamp = new Date().getTime();
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Usuario: username,
          Contrasenia: password,
          timestamp: loginTimestamp,
        }),
      });

      const responseData = await readJson(response);

      if (!response.ok) {
        if (response.status === 403 && responseData.error?.includes('cambiar su contraseña')) {
          setUserForPasswordChange(username);
          setShowChangePassword(true);
          setPassword('');
          setSuccessMessage('');
          return;
        }
        throw responseError(response, responseData, 'Credenciales incorrectas');
      }

      if (!responseData.token || !responseData.usuario) {
        throw new Error('Respuesta de autenticación inválida');
      }

      if (typeof responseData.token !== 'string' || responseData.token.length < 20) {
        throw new Error('Token de seguridad inválido');
      }

      establecerCookieSesion(responseData.token);
      saveRememberedUser(rememberUser, username);
      // Breve confirmación visual antes de entrar al portal
      setLoginSuccess(true);
      await new Promise((resolve) => setTimeout(resolve, 600));
      onLogin(responseData);
      setLoginSuccess(false);
      setUsername('');
      setPassword('');
      setSuccessMessage('');
    } catch (err) {
      const { kind, message } = describeError(err, 'Error al iniciar sesión. Verifique sus credenciales.');
      setErrorKind(kind);
      setError(message);
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  if (showChangePassword) {
    return (
      <ChangePassword
        username={userForPasswordChange}
        onPasswordChanged={handlePasswordChanged}
        onCancel={handleCancelPasswordChange}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 sm:p-10 relative"
      style={{ backgroundColor: FONDO, backgroundImage: BEAN_PATTERN }}
    >
      {sessionExpiredInfo?.show && (
        <div className="fixed inset-0 bg-cafe-800 bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-6 rounded-lg border-t-4 border-cafe-600 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-cafe-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-base font-semibold text-cafe-700">Sesión expirada</h3>
              </div>
            </div>
            <div className="mb-5">
              <p className="text-sm text-neutral-300 leading-relaxed">
                {sessionExpiredInfo.reason === 'inactivity'
                  ? 'Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente para continuar.'
                  : 'Su sesión ha expirado. Por favor, inicie sesión nuevamente para continuar.'}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setSessionExpiredInfo(null)}
                className="px-5 py-2 bg-cafe-700 text-white text-sm font-medium rounded-md hover:bg-cafe-800 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel café de bienvenida con la tarjeta del formulario montada sobre su borde derecho */}
      <div className="w-full max-w-5xl flex items-center justify-center animate-fade-in">
      {/* Panel de bienvenida */}
      <div
        className="hidden lg:flex lg:w-[60%] lg:min-h-[680px] flex-col justify-between rounded-3xl pl-12 pr-[21.5rem] py-12 text-left shadow-[0_30px_70px_-35px_rgba(59,39,25,0.55)]"
        style={{ background: CAFE_PANEL }}
      >
        {/* Logo en blanco, alineado a la izquierda con el saludo y centrado en altura en el espacio libre */}
        <div className="flex-1 flex items-center justify-start">
          <img
            src={logoBlanco}
            alt="Logo Fondo Cafetero"
            className="login-rise h-44 w-auto -ml-2.5"
            style={{ '--i': 0 }}
          />
        </div>

        {/* Fecha como antetítulo y saludo como titular, abajo a la izquierda */}
        <div>
          <p className="login-rise text-xs font-medium uppercase tracking-[0.2em] text-cafe-100/70 mb-4" style={{ '--i': 1 }}>
            {formatPanelDate(now)}
          </p>
          <h1 className="login-rise font-display text-5xl font-medium text-white leading-[1.05]" style={{ '--i': 2 }}>
            {getGreeting(now)}
          </h1>
        </div>
      </div>

      {/* Panel de formulario */}
      <div className="relative z-10 w-full max-w-md lg:-ml-[32%] flex items-center justify-center bg-white rounded-2xl shadow-[0_40px_90px_-25px_rgba(59,39,25,0.45)] px-6 py-12 sm:px-12">
        <div className="w-full max-w-sm text-left">
          <img
            src={logo}
            alt="Logo Fondo Cafetero"
            className="login-rise lg:hidden h-20 w-auto mb-6"
            style={{ '--i': 0 }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/150?text=FCN';
            }}
          />

          <div className="login-rise mb-9" style={{ '--i': 1 }}>
            {/* En pantallas pequeñas el panel se oculta: el saludo pasa a la tarjeta */}
            <p className="lg:hidden text-sm text-cafe-500 mb-1">{getGreeting(now)}</p>
            <h2 className="font-display text-[1.75rem] font-medium text-cafe-700 mb-2">
              Acceso al portal
            </h2>
            <p className="text-sm text-neutral-300">
              Ingrese sus credenciales para continuar.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-7">
            {successMessage && (
              <div className="bg-neutral-50 rounded-md px-4 py-3 flex items-start justify-between animate-fade-in">
                <div className="flex items-start">
                  <Shield className="h-4 w-4 text-cafe-600 mt-0.5 flex-shrink-0" />
                  <p className="text-cafe-700 text-sm ml-2.5">{successMessage}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSuccessMessage('')}
                  className="text-neutral-200 hover:text-neutral-400 ml-2 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {error && <ErrorAlert message={error} kind={errorKind} onClose={clearError} />}

            <div className="login-rise group" style={{ '--i': 2 }}>
              <label htmlFor="username" className="block text-sm font-medium text-cafe-700 mb-2 transition-colors duration-500 group-focus-within:text-cafe-600">
                Usuario
              </label>
              <div className="relative">
                <input
                  id="username"
                  ref={usernameRef}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={INPUT_CLASS}
                  autoComplete="username"
                  maxLength="50"
                  required
                />
              </div>
            </div>

            <div className="login-rise group" style={{ '--i': 3 }}>
              <label htmlFor="password" className="block text-sm font-medium text-cafe-700 mb-2 transition-colors duration-500 group-focus-within:text-cafe-600">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${INPUT_CLASS} pr-11`}
                  autoComplete="current-password"
                  maxLength="100"
                  autoFocus={Boolean(rememberedUser)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-neutral-200 hover:text-cafe-600 hover:bg-cafe-100/40 transition-colors duration-200"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Recordar usuario y recuperar contraseña en la misma fila, bajo el campo */}
              <div className="mt-4 flex items-center justify-between">
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none group/remember">
                  <input
                    type="checkbox"
                    checked={rememberUser}
                    onChange={(e) => setRememberUser(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span className="flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border border-cafe-200 bg-[#FBF8F5] text-white transition-all duration-200 group-hover/remember:border-cafe-400 peer-checked:border-[#432918] peer-checked:bg-[#432918] peer-focus-visible:ring-4 peer-focus-visible:ring-cafe-200/40">
                    <Check className={`h-3 w-3 transition-transform duration-200 ${rememberUser ? 'scale-100' : 'scale-0'}`} strokeWidth={3} />
                  </span>
                  <span className="text-xs text-cafe-700">Recordar mi usuario</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowPasswordResetModal(true)}
                  className="text-xs font-medium text-cafe-500 underline-offset-4 hover:text-cafe-700 hover:underline transition-colors duration-200"
                  disabled={isLoading}
                >
                  ¿Olvidó su contraseña?
                </button>
              </div>
            </div>

            <div className="login-rise !mt-8" style={{ '--i': 4 }}>
              <button
                type="submit"
                className={`group w-full flex items-center justify-center py-3 bg-[#432918] rounded-xl text-white text-sm font-semibold tracking-wide shadow-[0_1px_2px_rgba(0,0,0,0.15),0_10px_24px_-10px_rgba(62,39,35,0.6)] hover:bg-cafe-800 hover:-translate-y-px hover:shadow-[0_1px_2px_rgba(0,0,0,0.15),0_14px_28px_-10px_rgba(62,39,35,0.7)] active:translate-y-0 active:scale-[0.99] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:scale-100 ${loginSuccess ? 'disabled:opacity-100' : ''}`}
                disabled={isLoading}
              >
                {loginSuccess ? (
                  <span className="flex items-center animate-fade-in">
                    <Check className="h-4 w-4 mr-2 text-sol-300" />
                    Acceso concedido
                  </span>
                ) : isLoading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2.5"></span>
                    Verificando...
                  </>
                ) : (
                  <>
                    Iniciar sesión
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>

            <div className="login-rise" style={{ '--i': 5 }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px flex-1 bg-cafe-200/50" />
                <span className="text-xs text-neutral-300">¿No tiene cuenta?</span>
                <span className="h-px flex-1 bg-cafe-200/50" />
              </div>
              <button
                type="button"
                onClick={() => setShowCredentialModal(true)}
                className="w-full py-3 text-sm font-medium text-cafe-700 border border-cafe-200/60 rounded-xl hover:border-cafe-300 hover:bg-cafe-100/50 transition-colors duration-200 disabled:opacity-50"
                disabled={isLoading}
              >
                Solicitar acceso
              </button>
            </div>
          </form>

        </div>
      </div>
      </div>

      <p className="login-rise mt-8 text-xs text-cafe-500" style={{ '--i': 7 }}>
        © {now.getFullYear()} Fondo Cafetero Nacional
      </p>

      <CredentialRequestModal
        show={showCredentialModal}
        onClose={() => setShowCredentialModal(false)}
      />

      <PasswordResetModal
        show={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
      />
    </div>
  );
};

export default Login;